import * as turf from '@turf/turf';
import { FeatureCollection, Feature } from 'geojson';

export interface SimplifyOptions {
  tolerance: number;
  highQuality?: boolean;
  mutate?: boolean;
}

export interface SimplifyResult {
  simplified: FeatureCollection;
  originalVertexCount: number;
  simplifiedVertexCount: number;
  reductionPercent: number;
}

/**
 * Simplify geometries using Douglas-Peucker algorithm
 * Works on LineString and Polygon geometries
 */
export function simplifyGeometries(
  geojson: FeatureCollection,
  options: SimplifyOptions
): SimplifyResult {
  const { tolerance, highQuality = false } = options;
  
  const originalVertexCount = countVertices(geojson);
  const simplified: Feature[] = [];
  
  geojson.features.forEach((feature) => {
    try {
      const geomType = feature.geometry.type;
      
      // Only simplify lines and polygons
      if (geomType === 'LineString' || geomType === 'MultiLineString' ||
          geomType === 'Polygon' || geomType === 'MultiPolygon') {
        
        const result = turf.simplify(feature, {
          tolerance,
          highQuality
        });
        
        simplified.push({
          ...result,
          properties: {
            ...feature.properties,
            _simplified: true,
            _tolerance: tolerance
          }
        });
      } else {
        // Keep points unchanged
        simplified.push(feature);
      }
    } catch (error) {
      // Skip features that cannot be simplified
      // Keep original if simplification fails
      simplified.push(feature);
    }
  });
  
  const simplifiedCollection: FeatureCollection = {
    type: 'FeatureCollection',
    features: simplified,
    ...(geojson as any).crs && { crs: (geojson as any).crs }
  } as FeatureCollection;
  
  const simplifiedVertexCount = countVertices(simplifiedCollection);
  const reductionPercent = ((originalVertexCount - simplifiedVertexCount) / originalVertexCount) * 100;
  
  return {
    simplified: simplifiedCollection,
    originalVertexCount,
    simplifiedVertexCount,
    reductionPercent
  };
}

/**
 * Count total vertices in a FeatureCollection
 */
function countVertices(geojson: FeatureCollection): number {
  let count = 0;
  
  geojson.features.forEach((feature) => {
    count += countFeatureVertices(feature);
  });
  
  return count;
}

/**
 * Count vertices in a single feature
 */
function countFeatureVertices(feature: Feature): number {
  const coords = (feature.geometry as any).coordinates;
  const geomType = feature.geometry.type;
  
  switch (geomType) {
    case 'Point':
      return 1;
    case 'LineString':
    case 'MultiPoint':
      return (coords as any[]).length;
    case 'Polygon':
      return (coords as any[][]).reduce((sum, ring) => sum + ring.length, 0);
    case 'MultiLineString':
      return (coords as any[][]).reduce((sum, line) => sum + line.length, 0);
    case 'MultiPolygon':
      return (coords as any[][][]).reduce((sum, polygon) => 
        sum + polygon.reduce((pSum, ring) => pSum + ring.length, 0), 0
      );
    default:
      return 0;
  }
}

/**
 * Get recommended tolerance based on geometry bounds
 */
export function getRecommendedTolerance(
  geojson: FeatureCollection,
  simplificationLevel: 'low' | 'medium' | 'high' = 'medium'
): number {
  const bbox = turf.bbox(geojson);
  const width = bbox[2] - bbox[0];
  const height = bbox[3] - bbox[1];
  const maxExtent = Math.max(width, height);
  
  // Calculate tolerance as percentage of extent
  const percentages = {
    low: 0.0001,    // 0.01% - minimal simplification
    medium: 0.001,  // 0.1% - moderate simplification
    high: 0.01      // 1% - aggressive simplification
  };
  
  return maxExtent * percentages[simplificationLevel];
}

/**
 * Validate simplification tolerance
 */
export function validateTolerance(
  tolerance: number,
  geojson: FeatureCollection
): { valid: boolean; message?: string } {
  if (tolerance <= 0) {
    return {
      valid: false,
      message: 'Tolerance must be greater than 0'
    };
  }
  
  const bbox = turf.bbox(geojson);
  const width = bbox[2] - bbox[0];
  const height = bbox[3] - bbox[1];
  const maxExtent = Math.max(width, height);
  
  // Warn if tolerance is too large (>10% of extent)
  if (tolerance > maxExtent * 0.1) {
    return {
      valid: false,
      message: 'Tolerance is too large and may result in severe geometry distortion'
    };
  }
  
  return { valid: true };
}

/**
 * Preview simplification impact
 */
export function previewSimplification(
  geojson: FeatureCollection,
  tolerance: number
): {
  estimatedReduction: number;
  affectedFeatures: number;
  totalFeatures: number;
} {
  let affectedFeatures = 0;
  
  geojson.features.forEach((feature) => {
    const geomType = feature.geometry.type;
    if (geomType === 'LineString' || geomType === 'MultiLineString' ||
        geomType === 'Polygon' || geomType === 'MultiPolygon') {
      affectedFeatures++;
    }
  });
  
  // Rough estimation: typically 30-70% reduction depending on tolerance
  const estimatedReduction = Math.min(70, tolerance * 10000);
  
  return {
    estimatedReduction,
    affectedFeatures,
    totalFeatures: geojson.features.length
  };
}
